import { GAME_CONFIG } from '../utils/constants.js';
import { Projectile } from './projectile.js';
import { showDamageNumber } from '../utils/helpers.js';

export class Player {
    constructor(assetManager) {
        this.x = 0;
        this.y = 0;
        this.width = GAME_CONFIG.PLAYER.SIZE;
        this.height = GAME_CONFIG.PLAYER.SIZE;
        this.speed = GAME_CONFIG.PLAYER.SPEED;
        this.dashSpeed = GAME_CONFIG.PLAYER.DASH_SPEED;
        this.isDashing = false;
        this.currentSpeed = this.speed;
        this.dashDuration = GAME_CONFIG.PLAYER.DASH_DURATION;
        this.dashTimer = 0;
        this.dashTrail = [];
        this.multiShotActive = false;
        this.multiShotTimer = 0;
        this.rocketActive = false;
        this.rocketTimer = 0;

        // Animation properties
        this.frames = assetManager.getSprite('playerBlue');
        if (!this.frames || !Array.isArray(this.frames) || this.frames.length === 0) {
            console.error('Player sprite frames missing or empty. Using placeholder.');
            // Try to use any available sprite as fallback
            const allSprites = Array.from(assetManager.assets.sprites.values());
            this.frames = allSprites.length > 0 ? allSprites[0] : [document.createElement('canvas')];
        }

        this.currentFrame = 0;
        this.frameCount = this.frames.length;
        this.frameDelay = 25;
        this.frameTimer = 0;

        this.canvas = document.getElementById('gameCanvas');

        // Reset position
        this.resetPosition();
    }

    resetPosition() {
        this.x = this.canvas.width / 2;
        this.y = this.canvas.height - 100;
    }

    update(gameState, keys) {
        // Update dash trail
        if (this.isDashing) {
            this.dashTrail.push({x: this.x, y: this.y});
            if (this.dashTrail.length > 5) {
                this.dashTrail.shift();
            }
        } else {
            this.dashTrail = [];
        }

        // Handle dash
        if ((keys.ShiftLeft || keys.ShiftRight) && gameState.dashCooldown === 0 && !this.isDashing) {
            this.isDashing = true;
            this.dashTimer = this.dashDuration;
            gameState.dashCooldown = GAME_CONFIG.DASH.MAX_COOLDOWN;
        }

        const baseSpeed = this.speed * (gameState.stats.speedMult ?? 1);
        if (this.isDashing) {
            this.currentSpeed = baseSpeed + (this.dashSpeed - baseSpeed) *
                              (this.dashTimer / this.dashDuration);
            this.dashTimer--;
            if (this.dashTimer <= 0) {
                this.isDashing = false;
            }
        } else {
            this.currentSpeed = baseSpeed;
        }

        // Update multishot timer
        if (this.multiShotActive) {
            this.multiShotTimer -= 16; // approx 60fps
            if (this.multiShotTimer <= 0) {
                this.multiShotActive = false;
            }
        }

        // Update rocket barrage timer
        if (this.rocketActive) {
            this.rocketTimer -= 16; // approx 60fps
            if (this.rocketTimer <= 0) {
                this.rocketActive = false;
            }
        }

        // Passive shield regen (upgrade stat)
        if (gameState.stats.shieldRegen > 0) {
            gameState.health = Math.min(
                gameState.stats.maxHealth,
                gameState.health + gameState.stats.shieldRegen
            );
        }

        // Update movement
        if (keys.ArrowLeft && this.x > 0) this.x -= this.currentSpeed;
        if (keys.ArrowRight && this.x < this.canvas.width - this.width) this.x += this.currentSpeed;
        if (keys.ArrowUp && this.y > 0) this.y -= this.currentSpeed;
        if (keys.ArrowDown && this.y < this.canvas.height - this.height) this.y += this.currentSpeed;

        // Clamp position to canvas bounds
        this.x = Math.max(0, Math.min(this.x, this.canvas.width - this.width));
        this.y = Math.max(0, Math.min(this.y, this.canvas.height - this.height));

        // Ensure frames are valid
        if (!this.frames || !Array.isArray(this.frames) || this.frames.length === 0) return;

        // Update animation
        this.frameTimer++;
        if (this.frameTimer >= this.frameDelay) {
            this.currentFrame = (this.currentFrame + 1) % this.frameCount;
            this.frameTimer = 0;
        }
    }

    shoot(gameState, game) {
        if (gameState.energy >= GAME_CONFIG.PLAYER.SHOOT_COST) {
            // Get projectile from pool or create new one
            const projectile = game.objectPools.getFromPool('projectiles') ||
                new Projectile(this.x, this.y - this.height / 2, 'player', game);
            projectile.reset(this.x, this.y - this.height / 2, 'player');
            gameState.projectiles.push(projectile);

            // Handle multishot (powerup or permanent upgrade)
            if (this.multiShotActive || gameState.stats.multiShotPermanent) {
                [-0.2, 0.2].forEach(angle => {
                    const extraProj = game.objectPools.getFromPool('projectiles') ||
                        new Projectile(this.x, this.y - this.height / 2, 'player', game);
                    extraProj.reset(this.x, this.y - this.height / 2, 'player', angle);
                    gameState.projectiles.push(extraProj);
                });
            }

            // Handle rocket barrage (powerup): heavy piercing shot alongside the normal one
            if (this.rocketActive) {
                const rocket = game.objectPools.getFromPool('projectiles') ||
                    new Projectile(this.x, this.y - this.height / 2, 'player', game);
                rocket.reset(this.x, this.y - this.height / 2, 'player', 0, true);
                gameState.projectiles.push(rocket);
            }
            gameState.energy = Math.max(0, gameState.energy - GAME_CONFIG.PLAYER.SHOOT_COST);

            // Update energy bar UI
            const energyBar = document.getElementById('energyBar');
            if (energyBar) {
                energyBar.style.width = `${gameState.energy}%`;
            }

            // Play sound effect
            const laserSound = game.assetManager.getAudio('laser');
            if (laserSound) {
                laserSound.volume = 0.5;
                laserSound.currentTime = 0;
                laserSound.play();
            }

            return true;
        }
        return false;
    }

    takeDamage(amount, gameState) {
        gameState.health = Math.max(0, gameState.health - amount);

        // Update health bar UI
        const healthBar = document.getElementById('healthBar');
        if (healthBar) {
            healthBar.style.width = `${gameState.health}%`;
        }

        // Add screen flash effect
        document.getElementById('gameContainer').classList.add('damage-flash');
        setTimeout(() => {
            document.getElementById('gameContainer').classList.remove('damage-flash');
        }, 100);

        // Show damage number
        this.showDamageNumber(amount);

        return gameState.health <= 0;
    }

    showDamageNumber(amount) {
        showDamageNumber(amount, this.x, this.y);
    }
}

export default Player;